import Head from 'next/head';
import PropTypes from 'prop-types';
import { NextSeo } from 'next-seo';
import Questions from '@cont/Saved/Questions';
import { useSelectorToStore } from '@hooks/reduxHooks';
import { fetchTopologyInfo } from '@actions/topologies';
import { fetchSavedEntities } from '@actions/saved';
import { useSetSelectedNav } from '@stores/sidenav';
import { getQuizSolutionFeedbackOptions } from '@selectors/quizTaking';
import { fetchSolutionFeedbackOptions } from '@actions/quizTaking';
import { fetchAllLanguages } from '@actions/educator';
import isEmptyObject from '@utils/isEmptyObject';
import setupInitialProps from '@utils/setupInitialProps';
import Layout from '@layout/index';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';

const QuestionsPage = ({ goalUID }) => {
  const goalInfo = useSelectorToStore(
    ({ topologies }) => topologies.topologyDetails[goalUID]
  );
  const { goalSlug, title, uid } = goalInfo;
  const goalTitle = 'My Saved Questions';
  const goalDescription = `Prepare live from Top Educators with comprehensive, structured, and crash courses along with practice section for ${title} on Unacademy Plus.`;
  const canonicalURL = `https://unacademy.com/goal/${goalSlug}/${uid}/questions`;
  const imageURL =
    'https://static.uacdn.net/static/dist/c76c2e18c7980818a913f9467c74c567.png';

  useSetSelectedNav('saved');

  return (
    <>
      <NextSeo
        title={goalTitle}
        description={goalDescription}
        openGraph={{
          title: goalTitle,
          description: goalDescription,
          images: [
            {
              url: imageURL
            }
          ],
          url: canonicalURL
        }}
      />
      <Head>
        <title>{goalTitle}</title>
      </Head>
      <Questions />
    </>
  );
};

QuestionsPage.pageInfo = {
  isMWebSupported: true,
  name: 'Saved Questions List',
  layoutOptions: {
    showPlusNav: true
  }
};

export default QuestionsPage;

QuestionsPage.getInitialProps = setupInitialProps(
  async ({ store, query, isServer }) => {
    const { goalUID } = query;
    const promises = [];

    if (!store.getState().topologies?.topologyDetails?.[goalUID])
      promises.push(store.dispatch(fetchTopologyInfo(goalUID)));

    if (!store.getState().topologies.allLanguages?.length)
      promises.push(store.dispatch(fetchAllLanguages()));

    const dataKey = `${goalUID}-questions`;

    if (!store.getState().paginator?.saved?.[dataKey]?.loaded)
      promises.push(
        store.dispatch(
          fetchSavedEntities({ goalUid: goalUID, entityType: 3, key: dataKey })
        )
      );
    if (isEmptyObject(getQuizSolutionFeedbackOptions(store.getState()))) {
      promises.push(store.dispatch(fetchSolutionFeedbackOptions()));
    }
    if (promises.length) await Promise.all(promises);
    return {
      isServer,
      goalUID
    };
  },
  QuestionsPage.pageInfo
);

QuestionsPage.propTypes = {
  goalUID: PropTypes.string.isRequired
};
QuestionsPage.Layout = Layout;
QuestionsPage.Wrapper = PageReduxProvider;
